import { useEffect, useState } from "react";
import "./CreateRequestWorkflow.scss";
import ApiService from "../../services/apiService";
import URLS from "../../urls/Urls";
import { useSelector } from "react-redux";
import { userDetails } from "../../redux/user/userSelector";
import { FilterOptionSchema } from "../../components/Filter/filter.data";
import Filter from "../../components/Filter/Filter";
import { useNavigate } from "react-router-dom";
import {
  FormDetail,
  OptionsSchema,
  QuestionDetail,
  VendorFormResponse,
} from "../../models/VendorForm";
import CustomQuestionFormResponse from "../../components/CustomQuestionFormResponse/CustomQuestionFormResponse";

const CreateRequestWorkflow = () => {
  const user = useSelector(userDetails);
  const navigate = useNavigate();
  const [department, setDepartment] = useState("");
  const [budget, setBudget] = useState("");
  const [requestName, setRequestName] = useState("");
  const [requested, setRequested] = useState("xs");
  const [workflows, setWorkflows] = useState<FilterOptionSchema[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<
    FilterOptionSchema[]
  >([]);
  const [files, setFiles] = useState<File[]>([]);
  const [formDetails, setFormDetails] = useState<FormDetail | null>(null);
  const [formResponse, setFormResponse] = useState<VendorFormResponse>();
  const [fileMap, setFileMap] = useState<{ [key: number]: File[] }>({});
  const [formData, setFormData] = useState<FormData>(new FormData());

  useEffect(() => {
    getWorkflows();
  }, []);

  const getWorkflows = async () => {
    try {
      const { data } = await ApiService().client.get(URLS.WORKFLOW.LIST);
      if (data) {
        setWorkflows(data.workflows);
      }
    } catch (error) {
      console.error("Error fetching workflow details");
    }
  };

  const getFormDetail = async (id: number) => {
    const { data } = await ApiService().client.get(
      URLS.WORKFLOW.FORM_DETAIL(id)
    );
    setFormDetails(data.form_detail);
    const questions: QuestionDetail[] = data.form_detail.questions;
    const initialResponses = questions.map((question) => ({
      question_id: question.id,
      input_type: question.input_type,
      responses: [],
    }));

    const initialFormResponse: VendorFormResponse = {
      vendor_form_id: id,
      responses: initialResponses,
    };

    setFormResponse(initialFormResponse);
  };

  const formatDateIndian = (dateString: string): string => {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
      const displayNames = files.map((file) => file.name);
      const fileList: File[] = [];
      const displayNameList: string[] = [];
      files.forEach((file, index) => {
        fileList.push(file);
        const displayName = displayNames[index]?.trim();
        if (displayName) {
          displayNameList.push(displayName);
        }
      });

      setFiles(fileList);
    }
  };

  useEffect(() => {
    const newFormData = new FormData();

    Object.keys(fileMap).forEach((questionId) => {
      const files = fileMap[parseInt(questionId)];
      files.forEach((file) => {
        newFormData.append("questions[]", questionId);
        newFormData.append("files[]", file);
        newFormData.append("fileNames[]", file.name);
      });
    });

    setFormData(newFormData);
  }, [fileMap]);

  const handleSubmit = async () => {
    if (selectedWorkflow.length <= 0) {
      return;
    }
    const requestDetails = {
      name: requestName,
      workflow_id: selectedWorkflow[0].id,
      department,
      budget,
    };
    formData.append("formResponse", JSON.stringify(formResponse));
    formData.append("requestDetails", JSON.stringify(requestDetails));
    try {
      const { data } = await ApiService().client.post(
        URLS.WORKFLOW.REQUESTS,
        formData
      );
    } catch (error) {
      console.error("Error submitting form", error);
    }
  };

  const onQuestionResponseCapture = (
    question: QuestionDetail,
    response: string[] | File[] | OptionsSchema[]
  ) => {
    if (response[0] instanceof File) {
      setFileMap((prevFileMap) => ({
        ...prevFileMap,
        [question.id]: response as File[],
      }));
    } else {
      setFormResponse((prevFormResponse) => {
        if (!prevFormResponse) return undefined;

        const updatedResponses = prevFormResponse.responses.map((res) => {
          if (res.question_id === question.id) {
            return {
              ...res,
              responses: response,
            };
          }
          return res;
        });

        return {
          ...prevFormResponse,
          responses: updatedResponses,
        };
      });
    }
  };

  const navigateToRequestList = () => {
    navigate("/requests");
  };

  return (
    <div className="workflow-create-request-page full-width flex-column ">
      <div className="upper-bar xetgo-font-tag px-16 py-12 flex-row gap-4">
        <p onClick={navigateToRequestList} className="bold cursor-pointer">
          Requests
        </p>
        <p style={{ color: "#757575" }}> / Create</p>
      </div>
      <div className="workflow-request-create-lower-main-container border-box flex-1 px-16 py-16">
        <div className="workflow-request-create-lower-container border-box full-height flex-row gap-16 p-16">
          <div className="detail-form-main-container flex-column gap-16 p-16 flex-1 overflow-scroll">
            <div className="flex-column gap-8">
              <p className="xetgo-font-button bolder">Request Details</p>
              <p style={{ color: "#333333" }} className="xetgo-font-button ">
                Please Fill out the Request details
              </p>
            </div>
            <div className="question-main-container xetgo-font-tag p-16 flex-column gap-12">
              <p>Request Name</p>
              <input
                className="answer-input border-box px-12 py-8 xetgo-font-tag"
                placeholder="Enter name"
                value={requestName}
                onChange={(e) => setRequestName(e.target.value)}
              />
            </div>
            <div className="question-main-container xetgo-font-tag p-16 flex-column gap-12">
              <p>Workflow</p>
              <Filter
                option={{
                  key: "form",
                  name: "Workflow",
                }}
                optionList={workflows || []}
                multipleSelection={false}
                onSelection={(val, _option) => {
                  if (val.length > 0) {
                    setSelectedWorkflow(val);
                    getFormDetail(val[0].id);
                  } else {
                    setSelectedWorkflow([]);
                  }
                }}
                preSelected={selectedWorkflow}
                mode="showNames"
                showPlaceholder={true}
                direction="left"
                addNew={false}
                classname="answer-input border-box"
              />
            </div>
            <div className="question-main-container xetgo-font-tag p-16 flex-column gap-12">
              <p>Department</p>
              <input
                className="answer-input border-box px-12 py-8 xetgo-font-tag"
                placeholder="Enter department"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              />
            </div>
            <div className="question-main-container xetgo-font-tag p-16 flex-column gap-12">
              <p>Budget</p>
              <input
                className="answer-input border-box px-12 py-8 xetgo-font-tag"
                placeholder="Enter Budget"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
              />
            </div>
            <div className="question-main-container xetgo-font-tag p-16 flex-column gap-12">
              <p>Requestor</p>
              <input
                className="answer-input border-box px-12 py-8 xetgo-font-tag"
                placeholder="Enter Requester"
                value={user.name || ""}
                disabled
              />
            </div>
            <div className="question-main-container xetgo-font-tag p-16 flex-column gap-12">
              <p>Created Date</p>
              <input
                className="answer-input border-box px-12 py-8 xetgo-font-tag"
                placeholder="Enter name"
                value={formatDateIndian(new Date().toISOString())}
                disabled
              />
            </div>
            <div className="question-main-container xetgo-font-tag p-16 flex-column gap-12">
              <p>Attachments</p>
              <div className="cursor-pointer flex-1 flex-column gap-8">
                <input
                  id="multiple-fileInput"
                  name="file"
                  type="File"
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "12px",
                    display: "none",
                  }}
                  onChange={(event) => {
                    handleFileChange(event);
                  }}
                  multiple={true}
                />
                <label
                  htmlFor="multiple-fileInput"
                  className="answer-input cursor-pointer flex-row align-items-center p-12 xetgo-font-tag flex-row full-width border-box "
                >
                  <p className="xetgo-font-tag flex-row justify-content-space-between disable-cursor full-width">
                    <p>
                      {files.length === 0
                        ? "Upload File"
                        : files.map((file, index) => (
                            <span key={index}>
                              {file.name}
                              {index < files.length - 1 ? ", " : "."}
                            </span>
                          ))}
                    </p>
                    <img
                      className="upload-icon"
                      height={16}
                      width={16}
                      src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20240828115442-upload+%287%29.svg"
                      alt="upload-icon"
                    />
                  </p>
                </label>
              </div>
            </div>
          </div>
          {selectedWorkflow.length > 0 && formDetails && (
            <div className="detail-form-main-container flex-column gap-16 p-16 flex-1 overflow-scroll">
              <div className="flex-column gap-8">
                <p className="xetgo-font-button bolder">{formDetails.title}</p>
                <p style={{ color: "#333333" }} className="xetgo-font-button ">
                  {formDetails.description}
                </p>
              </div>
              {formDetails.questions.map((question, questionIndex) => (
                <CustomQuestionFormResponse
                  key={questionIndex}
                  questionDetails={question}
                  onValueChange={onQuestionResponseCapture}
                  disabled={false}
                />
              ))}
            </div>
          )}
          {selectedWorkflow.length === 0 && (
            <div className="detail-form-main-container flex-column gap-16 p-16 flex-1">
              <div className="empty-state-main-container flex-column gap-48 full-height full-width align-items-center justify-content-center">
                <img
                  height={243}
                  width={235}
                  src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250304110621-Group.svg"
                  alt="empty-icon"
                />
                <div className="text-containers flex-column gap-12 full-width ">
                  <p style={{ color: "#333333" }} className="bold">
                    Your form is empty, No Questions Added Yet!
                  </p>
                  <p style={{ color: "#3333336B" }}>
                    {" "}
                    Start by selecting a workflow to display the form.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="edit-user-btns-main-container flex-row align-items-center border-box justify-content-space-between full-width p-16">
          <p
            onClick={() => navigate("/requests")}
            className="ave-btn back px-12 py-8 xetgo-font-tag bold cursor-pointer"
          >
            Back
          </p>
          <div className="btns-container flex-row gap-12">
            <div
              // onClick={() => onCancel()}
              className="save-btn cancel-btn-mob px-12 py-8 xetgo-font-tag bold cursor-pointer"
            >
              Save
            </div>
            <div
              onClick={handleSubmit}
              className="save-btn save px-12 py-8 xetgo-font-tag bold cursor-pointer"
            >
              Submit
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateRequestWorkflow;
